import React, { useCallback, useMemo, useState } from 'react';
import type { AdminPermissionGroups as AdminGroups } from 'c-admin';
import type { CardHeaderProps } from '@mui/material';
import { Card, CardContent, CardHeader, Collapse, Grid, Stack } from '@mui/material';
import { TransparentCard } from 'c-components';
import { KeyboardArrowDown } from '@mui/icons-material';
import { cssRotation } from 'c-lib';
import type { Role } from 'c-entity-types';
import { adminPermissionLabel } from 'c-admin/Lib';
import PermissionCheckbox from './PermissionCheckbox';
import SelectAllPermissions from './SelectAllPermissions';
import EntityPermissionChangeCounts from './EntityPermissionChangeCounts';

type Props = {
    role: Role;
    groups: AdminGroups;
};

/**
 * The `admin` namespace rendered the same way as the entity namespaces: one collapsible card, with
 * a tile per group (campaign, page, data_status, ...) inside it.
 */
const AdminPermissionGroups: React.FC<Props> = ({ role, groups }) => {
    const [open, setOpen] = useState(false);
    const onHeaderClick = useCallback(() => setOpen(o => !o), [setOpen]);

    const cardHeaderProps = useMemo<CardHeaderProps>(
        () => ({ sx: { cursor: 'pointer' }, onClick: onHeaderClick }),
        [onHeaderClick],
    );

    const groupNames = useMemo(() => Object.keys(groups).sort(), [groups]);
    const allPermissionIds = useMemo(
        () => groupNames.reduce<number[]>((acc, g) => [...acc, ...groups[g].map(p => p.id)], []),
        [groupNames, groups],
    );

    if (allPermissionIds.length === 0) return null;

    return (
        <TransparentCard
            title="admin"
            titleTypographyProps={{ variant: 'h4' }}
            cardHeaderProps={cardHeaderProps}
            controls={[
                <EntityPermissionChangeCounts key="counts" ids={allPermissionIds} role={role} />,
                <SelectAllPermissions key="select-all" ids={allPermissionIds} />,
                <KeyboardArrowDown key="arrow" css={cssRotation({ rotate: open })} />,
            ]}
        >
            <Collapse in={open} unmountOnExit={false} mountOnEnter={false}>
                <CardContent>
                    <Grid container spacing={4}>
                        {groupNames.map(group => (
                            <Grid item xs={12} sm={6} md={4} xl={3} key={group}>
                                <Card elevation={2}>
                                    <CardHeader
                                        title={group.replace(/_/g, ' ')}
                                        titleTypographyProps={{ sx: { wordBreak: 'break-all' } }}
                                        action={
                                            <SelectAllPermissions
                                                ids={groups[group].map(p => p.id)}
                                            />
                                        }
                                    />
                                    <CardContent>
                                        <Stack>
                                            {groups[group].map(permission => (
                                                <PermissionCheckbox
                                                    key={permission.name}
                                                    permission={permission}
                                                    label={adminPermissionLabel(
                                                        permission.name,
                                                        group,
                                                    )}
                                                />
                                            ))}
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </CardContent>
            </Collapse>
        </TransparentCard>
    );
};

export default React.memo(AdminPermissionGroups);
